import * as filterAPI from '@apps-common/js-client/api/filter';
import * as itemsAPI from '@apps-common/js-client/api/items';
import kinda from 'kinda';
import parseCoords from 'coord-parser';
import {fromLonLat} from 'ol/proj';
import {inverse, toPoint} from 'mgrs'; //ES6
import {
  fromCircle as polyfromFromCircle,
  fromExtent as polygonFromExtent,
} from 'ol/geom/Polygon';

import {BING_TOKEN} from '@explorer/config.js';
import {GEO_PROJECTION, WEB_PROJECTION} from '@explorer/constants';
import {getMinCircle, toGeoJSON} from '@explorer/util/geom.js';

export const SOURCE_TYPES = {
  GEOCODER: 'geocoder',
  MGRS_SQUARE: 'mgrsSquare',
  MGRS_POINT: 'mgrsPoint',
  ITEM_IDS: 'itemIds',
  SAVED_SEARCHES: 'savedSearches',
  LATLON: 'latlon',
};

export const latLonFromString = string => {
  let latLon = null;
  try {
    const obj = parseCoords(string);
    if (obj.lat >= -90 && obj.lat <= 90 && obj.lon >= -180 && obj.lon <= 180) {
      const lat = Math.round(obj.lat * 1e8) / 1e8;
      const lon = Math.round(obj.lon * 1e8) / 1e8;
      const latHemisphere = lat > 0 ? 'N' : 'S';
      const lonHemisphere = lon > 0 ? 'E' : 'W';

      latLon = {
        lat,
        lon,
        latHemisphere,
        lonHemisphere,
        label: `${Math.abs(lat)}° ${latHemisphere}, ${Math.abs(lon)}° ${lonHemisphere}`,
      };
    }
  } catch (err) {
    // pass;
  }
  return latLon;
};

export const testMGRS = query => {
  // check with spaces first
  const spacesMatch = query.match(
    /^[0-9]{1,2}[A-Z][A-Z][A-Z] ([0-9]{1,5}) ([0-9]{1,5})$/
  );
  if (spacesMatch && spacesMatch.length > 2) {
    if (spacesMatch[1].length === spacesMatch[2].length) {
      return spacesMatch[1].length;
    }
  }

  // no spaces
  const noSpaceMatch = query.match(/^[0-9]{1,2}[A-Z][A-Z][A-Z]([0-9]{2,10})$/);
  if (noSpaceMatch) {
    if (noSpaceMatch[1].length % 2 === 0) {
      return noSpaceMatch[1].length / 2;
    }
  }
  return -1;
};

export const suggestionSequence = [
  'addressLine',
  'locality',
  'adminDistrict',
  'countryRegion',
];

export const getSuggestionDescriptions = suggestion => {
  // Bing will provide the address parts for the suggestion
  // To map to our suggestion format, we will prioritize populating the description and put the remaining parts in descriptionDetails

  // Some suggestions have names (i.e. a named landmark like "Madison Square Garden")
  // For those results, the description is automatically that name.
  let title = suggestion.name || null;
  const descriptionDetails = [];
  const addr = suggestion.address;
  let query = title;

  suggestionSequence.forEach(item => {
    if (addr?.[item]) {
      if (!title) {
        // If description hasn't been set yet (the result doesn't have a name and no earlier part of the sequence was available) set the current part as the description
        title = addr[item];
        query = title;
      } else {
        // All other parts of the address should go into the details array
        // It's up to the implementation to get what they need from the array, i.e. a localized join into a string
        descriptionDetails.push(addr[item]);
        query += ' ' + addr[item];
      }
    }
  });

  return {
    title,
    descriptionDetails,
    description: descriptionDetails.join(', '),
    label: [title, ...descriptionDetails].join(', '),
    source: SOURCE_TYPES.GEOCODER,
    query,
  };
};

export const getSuggestions = (center, query) => {
  const url = `https://dev.virtualearth.net/REST/v1/Autosuggest?key=${BING_TOKEN}`;
  let params = `&query=${query}&includeEntityTypes=Place,Address`;

  if (center) {
    params = params + `&userLocation=${center[1]},${center[0]},5000`;
  }

  return fetch(`${url}${params}`)
    .then(r => r.json())
    .then(data => {
      const transformed = data.resourceSets?.[0].resources?.[0].value.map(r => {
        const description = getSuggestionDescriptions(r);

        return {
          type: 'suggestion',
          suggestion: r,
          ...description,
        };
      });

      return {
        features: transformed || [],
      };
    });
};

export const suggestMGRS = query => {
  const precision = testMGRS(query);
  if (precision < 0) {
    return [];
  } else if (precision <= 3) {
    const bbox = inverse(query);
    return [
      {
        title: `Grid square: ${query}`,
        source: SOURCE_TYPES.MGRS_SQUARE,
        geometry: toGeoJSON(polygonFromExtent(bbox)),
      },
    ];
  } else {
    const pt = toPoint(query);
    const circle = getMinCircle(fromLonLat(pt));
    return [
      {
        title: `MGRS Point: ${query}`,
        source: SOURCE_TYPES.MGRS_POINT,
        geometry: toGeoJSON(
          polyfromFromCircle(circle).transform(WEB_PROJECTION, GEO_PROJECTION)
        ),
        showCenterMarker: true,
      },
    ];
  }
};

/**
 * Generate a list of items whose identifiers match the given query.
 * @param {string} query A query string.
 * @return {Promise<Array>} A promise for a list of suggestions.
 */
export const suggestSearchItemIds = (query, itemTypes = []) => {
  let promise;
  if (itemTypes.length > 0 && query.length > 10 && query.indexOf(' ') === -1) {
    promise = itemsAPI
      .search({
        types: itemTypes,
        filter: filterAPI.strings('id', [query]),
      })
      .then(features => {
        return features.map(item => ({
          title: item.id,
          description: item.properties.item_type,
          source: SOURCE_TYPES.ITEM_IDS,
          searchItem: item,
        }));
      });
  } else {
    promise = Promise.resolve([]);
  }
  return promise;
};

/**
 * Generate a list of saved searches that match the given query.
 * @param {string} query A query string.
 * @return {Array} A list of any matching saved searches.
 */
export const suggestSavedSearches = (query, searches) => {
  const suggestions = [];
  // suggest any matching saved searches
  if (query.length > 3 && searches) {
    const ranked = searches
      .map(search => ({
        search: search,
        match: kinda(query, search.name),
      }))
      .sort((a, b) => (a.match > b.match ? 1 : -1));

    ranked.forEach(item => {
      if (item.match > 0.3) {
        suggestions.unshift({
          title: item.search.name,
          description: 'Saved search',
          source: SOURCE_TYPES.SAVED_SEARCHES,
          savedSearch: item.search,
          search: true,
        });
      }
    });
  }
  return suggestions;
};

/**
 * Generate a list of suggestions if the given query looks like a lat, lon pair.
 * @param {string} query A query string.
 * @return {Array} A list of one suggestion if the query is a lat, lon pair.
 */
export const suggestLatLon = query => {
  const suggestions = [];
  if (!query) {
    return;
  }

  const latLon = latLonFromString(query);

  if (latLon) {
    const circle = getMinCircle(fromLonLat([latLon.lon, latLon.lat]));

    const geometry = toGeoJSON(polyfromFromCircle(circle), true);
    const suggestion = {
      ...latLon,
      geometry,
      source: SOURCE_TYPES.LATLON,
      showCenterMarker: true,
      title: latLon.label,
    };

    suggestions.push(suggestion);
  }

  return suggestions;
};

export const mapLatLonSuggestions = latlon => {
  const {lat, lon, latHemisphere, lonHemisphere, ...suggestion} = latlon;
  const context = {lat, lon, latHemisphere, lonHemisphere};

  suggestion.context = context;

  return suggestion;
};

export const isGoogleMapsUrl = query =>
  query.indexOf('https://www.google.com/maps/') === 0;
/**
 * Accept a Google Maps url and extract the lat/lon from it
 */
export const suggestFromGoogleMapsUrl = query => {
  if (isGoogleMapsUrl(query)) {
    const latLon = query
      .match(/@(\-?\d+(\.\d+)?),(\-?\d+(\.\d+)?)/gi)[0]
      .slice(1);

    return suggestLatLon(latLon);
  }
  return [];
};
